// src/components/StripeConnectOnboarding.tsx
import { useState, useEffect } from 'react';
import axios from 'axios';
import { stripeAPI } from '../services/api.ts';

interface StripeStatus {
  connected: boolean;
  onboarding_complete: boolean;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  account_id?: string;
}

export function StripeConnectOnboarding() {
  const [status, setStatus] = useState<StripeStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
  try {
    const response = await stripeAPI.checkConnectStatus();
    setStatus(response.data);
    setError(null);
  } catch (err: any) {
    console.error('Error checking Stripe status:', err);
    setError(err.response?.data?.error || 'Failed to check Stripe status');
  } finally {
    setLoading(false);
  }
};

  const handleConnect = async () => {
  setConnecting(true);
  setError(null);
  
  try {
    const response = await stripeAPI.createConnectAccount();
    
    if (response.data.onboarding_url) {
      window.location.href = response.data.onboarding_url;
    } else {
      setError('Failed to create Stripe Connect account');
      setConnecting(false);
    }
  } catch (err: any) {
    console.error('Error connecting Stripe:', err);
    setError(err.response?.data?.error || 'Failed to connect Stripe account');
    setConnecting(false);
  }
};

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm border">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-300 rounded w-3/4"></div>
          <div className="h-4 bg-gray-300 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  // Not connected yet
  if (!status?.connected) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-yellow-900 mb-2">
              Connect Your Bank Account
            </h3>
            <p className="text-yellow-800 mb-4">
              To receive payments from your clients, you need to connect your bank account through Stripe. 
              This is a secure process that takes just a few minutes.
            </p>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-4">
                {error}
              </div>
            )}
            <button
              onClick={handleConnect}
              disabled={connecting}
              className="bg-orange-400 text-white px-6 py-3 rounded-lg hover:bg-orange-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {connecting ? 'Connecting...' : 'Connect with Stripe'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Connected but onboarding not complete
  if (!status.onboarding_complete) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">
              Complete Your Stripe Setup
            </h3>
            <p className="text-blue-800 mb-4">
              You've started the process, but haven't finished yet. Complete your Stripe setup to start accepting payments.
            </p>
            <button
              onClick={handleConnect}
              disabled={connecting}
              className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 font-medium"
            >
              {connecting ? 'Loading...' : 'Continue Setup'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Fully connected and verified
  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-green-900 mb-2">
            Stripe Connected Successfully
          </h3>
          <p className="text-green-800 mb-4">
            Your bank account is connected and you're ready to receive payments from clients.
          </p>
          <div className="space-y-2 text-sm text-green-700">
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Charges enabled: {status.charges_enabled ? 'Yes' : 'No'}
            </div>
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Payouts enabled: {status.payouts_enabled ? 'Yes' : 'No'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}